import { useEffect, useRef } from 'react';
import { Item } from '../../domain/item';

/** Visible images outrank nearby prefetches; offscreen cards own no queue slot. */
export function useImageViewport(request: Item['requestImage']) {
  const element = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!request || !element.current) return;
    if (typeof IntersectionObserver === 'undefined') return request(0);
    let visible = false;
    let nearby = false;
    let priority: number | undefined;
    let release: (() => void) | undefined;
    const update = () => {
      const next = visible ? 0 : nearby ? 1 : undefined;
      if (next === priority) return;
      release?.();
      priority = next;
      release = next === undefined ? undefined : request(next);
    };
    const viewport = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      update();
    });
    const prefetch = new IntersectionObserver(
      ([entry]) => {
        nearby = entry.isIntersecting;
        update();
      },
      { rootMargin: '300px' },
    );
    viewport.observe(element.current);
    prefetch.observe(element.current);
    return () => {
      viewport.disconnect();
      prefetch.disconnect();
      release?.();
    };
  }, [request]);
  return element;
}
